import React from 'react'
import { ViewRoom, ViewTable } from '@xeetr/types/models'
import { useFormContext, useWatch } from 'react-hook-form'

import TableCard from './card/TableCard'

import {
  DndContext,
  closestCenter,
  DragEndEvent,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  DraggableAttributes,
} from '@dnd-kit/core'
import {
  arrayMove,
  SortableContext,
  useSortable,
  sortableKeyboardCoordinates,
} from '@dnd-kit/sortable'
import { SyntheticListenerMap } from '@dnd-kit/core/dist/hooks/utilities'

// utility used in both component and tests
export function reorderTables(
  tables: ViewTable[] | undefined,
  activeId: string,
  overId: string
): ViewTable[] {
  if (!tables) return []

  // keep a sorted copy for index calculations
  const sorted = [...tables].sort((a, b) => a.order - b.order)
  const oldIndex = sorted.findIndex((t) => t.id === activeId)
  const newIndex = sorted.findIndex((t) => t.id === overId)
  if (oldIndex === -1 || newIndex === -1) return tables

  const moved = arrayMove(sorted, oldIndex, newIndex)
  // update orders to match their new positions
  return moved.map((t, i) => ({ ...t, order: i }))
}

const SortableItem: React.FC<{
  id: string
  children: (props: { dragProps: DraggableAttributes | SyntheticListenerMap }) => React.ReactNode
}> = ({ id, children }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id,
  })

  const style: React.CSSProperties = {
    transform: transform ? `translate3d(${transform.x}px, ${transform.y}px, 0)` : undefined,
    transition,
    opacity: isDragging ? 0.5 : 1,
  }

  // Combine attributes and listeners for the handle
  const dragProps: DraggableAttributes | SyntheticListenerMap = { ...attributes, ...listeners }

  return (
    <div ref={setNodeRef} style={style}>
      {children({ dragProps })}
    </div>
  )
}

const TableContent = () => {
  const { control, setValue } = useFormContext<ViewRoom>()
  const tables = useWatch({ control, name: 'tables' })

  // ensure we're always rendering in order property order
  const sortedTables = React.useMemo(() => {
    return tables ? [...tables].sort((a, b) => a.order - b.order) : []
  }, [tables])

  const sensors = useSensors(
    useSensor(PointerSensor),
    // keyboard support not strictly needed but matches other usages
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  )

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event
    if (over && active.id !== over.id) {
      const newTables = reorderTables(tables, active.id as string, over.id as string)
      setValue('tables', newTables, { shouldDirty: true, shouldTouch: true, shouldValidate: true })
    }
  }

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
      <SortableContext items={sortedTables.map((t) => t.id)}>
        <div className="mt-2 flex flex-wrap gap-5">
          {sortedTables.map((table, tableIndex) => (
            <SortableItem key={table.id} id={table.id}>
              {({ dragProps }) => (
                <TableCard table={table} tableIndex={tableIndex} dragProps={dragProps} />
              )}
            </SortableItem>
          ))}
        </div>
      </SortableContext>
    </DndContext>
  )
}

export default TableContent
